import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import { Printer, Download, X, CheckCircle } from 'lucide-react';

interface ReceiptModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'sale' | 'subscription' | 'coaching' | 'payment' | 'seance';
    data: any;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ isOpen, onClose, type, data }) => {
    const componentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Recu_HopFitness_${new Date().getTime()}`
    });

    if (!isOpen) return null;

    const handleDownloadImage = async () => {
        if (componentRef.current) {
            try {
                const canvas = await html2canvas(componentRef.current, { scale: 2 });
                const image = canvas.toDataURL('image/png', 1.0);
                const link = document.createElement('a');
                link.download = `recu_hopfitness_${new Date().getTime()}.png`;
                link.href = image;
                link.click();
            } catch (error) {
                console.error("Erreur lors de la génération de l'image", error);
                alert("Erreur lors du téléchargement.");
            }
        }
    };

    const renderItems = () => {
        if (type === 'sale' && data.items) {
            return data.items.map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between text-sm py-1">
                    <span className="text-gray-700">{item.quantity}x {item.name}</span>
                    <span className="font-medium">{(item.price * item.quantity).toFixed(2)} FCFA</span>
                </div>
            ));
        } else if (type === 'subscription') {
            return (
                <>
                    <div className="flex justify-between text-sm py-1">
                        <span className="text-gray-700">Forfait : {data.planName || data.forfaitName}</span>
                    </div>
                    {data.periodicity && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Périodicité : {data.periodicity}</span>
                        </div>
                    )}
                    {data.duration && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Durée : {data.duration}</span>
                        </div>
                    )}
                    {data.startDate && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Début : {data.startDate}</span>
                        </div>
                    )}
                    {data.endDate && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Fin : {data.endDate}</span>
                        </div>
                    )}
                    {data.quantity && data.quantity > 1 && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Quantité : x{data.quantity}</span>
                        </div>
                    )}
                    {data.paymentMethod && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Paiement : {data.paymentMethod}</span>
                        </div>
                    )}
                    {data.hasInscription && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Frais d'inscription inclus</span>
                        </div>
                    )}
                </>
            );
        } else if (type === 'seance') {
            return (
                <>
                    <div className="flex justify-between text-sm py-1">
                        <span className="text-gray-700">Forfait Séance : {data.forfaitName}</span>
                    </div>
                    {data.sessionDate && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Date Séance : {data.sessionDate}</span>
                        </div>
                    )}
                    {data.sessionTime && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Heure Séance : {data.sessionTime}</span>
                        </div>
                    )}
                    {data.paymentMethod && (
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-gray-700">Paiement : {data.paymentMethod}</span>
                        </div>
                    )}
                </>
            );
        } else if (type === 'coaching') {
            return (
                <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-700">Coaching : {data.formulaName}</span>
                </div>
            );
        } else if (type === 'payment') {
            return (
                <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-700">Règlement / Prolongation</span>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle size={20} />
                        <h3 className="text-lg font-bold">Transaction Réussie</h3>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 bg-gray-100 flex justify-center">
                    {/* Zone de reçu à imprimer */}
                    <div 
                        ref={componentRef} 
                        className="bg-white p-8 shadow-sm w-full max-w-[320px] border border-gray-200 print:shadow-none print:border-none"
                        style={{ fontFamily: 'monospace' }}
                    >
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-black text-brand-dark uppercase tracking-wider mb-1">HopFitness</h2>
                            <p className="text-xs text-gray-500">Centre de remise en forme</p>
                            <p className="text-xs text-gray-500">123 Rue de la Forme, Ville</p>
                            <p className="text-xs text-gray-500">Tél: +123 456 789</p>
                        </div>

                        <div className="border-t border-b border-dashed border-gray-300 py-3 mb-4 space-y-1">
                            <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Date:</span>
                                <span>{new Date().toLocaleString('fr-FR')}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Reçu N°:</span>
                                <span>#{Math.floor(Math.random() * 100000).toString().padStart(6, '0')}</span>
                            </div>
                            {data.clientName && (
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Client:</span>
                                    <span>{data.clientName}</span>
                                </div>
                            )}
                        </div>

                        <div className="mb-6">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Détails</h4>
                            {renderItems()}
                        </div>

                        <div className="border-t border-dashed border-gray-300 pt-4 mb-6">
                            <div className="flex justify-between items-end">
                                <span className="text-sm font-bold text-gray-700 uppercase">Total TTC</span>
                                <span className="text-xl font-black text-brand-dark">{Number(data.total).toFixed(2)} FCFA</span>
                            </div>
                        </div>

                        <div className="text-center text-xs text-gray-400 mt-8">
                            <p>Merci de votre confiance !</p>
                            <p className="mt-1">Les articles ne sont ni repris ni échangés.</p>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t border-gray-100 bg-white grid grid-cols-2 gap-3">
                    <button 
                        onClick={() => handlePrint()}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-md transition-colors w-full"
                    >
                        <Printer size={18} />
                        Imprimer
                    </button>
                    
                    <button 
                        onClick={handleDownloadImage}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-accent text-white font-medium rounded-md transition-colors w-full"
                    >
                        <Download size={18} />
                        Partager
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReceiptModal;
